import { useEffect, useRef } from "react";
import { useSettings } from "context/SettingsContext";
import { THEME_COLOR_OPTIONS } from "constants/themeColors";
import { HiOutlineX } from "react-icons/hi";
import { MdOutlineLightMode, MdOutlineDarkMode } from "react-icons/md";
import { TbLanguage } from "react-icons/tb";

const SettingsPanel = ({ isOpen, onClose }) => {
  const {
    theme,
    toggleTheme,
    language,
    changeLanguage,
    themeColor,
    changeThemeColor,
  } = useSettings();
  const panelRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const isDark = theme === "dark";
  const isBn = language === "bn";

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-[60]" aria-hidden="true" />
      )}

      <div
        ref={panelRef}
        className={`fixed top-0 right-0 h-full w-72 z-[70] shadow-2xl transform transition-transform duration-300
          ${isOpen ? "translate-x-0" : "translate-x-full"}
          ${isDark ? "bg-gray-900 text-white" : "bg-white text-gray-800"}
        `}
      >
        <div
          className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}
        >
          <h2 className="text-base font-bold tracking-wide">
            {isBn ? "সেটিংস" : "Settings"}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition ${isDark ? "hover:bg-gray-700" : "hover:bg-gray-100"}`}
            aria-label="Close settings"
          >
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-6 flex flex-col gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TbLanguage className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                {isBn ? "ভাষা" : "Language"}
              </span>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => changeLanguage("bn")}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all
                  ${
                    language === "bn"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : isDark
                        ? "border-gray-600 text-gray-300 hover:border-blue-600"
                        : "border-gray-200 text-gray-600 hover:border-blue-600"
                  }`}
              >
                বাংলা
              </button>
              <button
                onClick={() => changeLanguage("en")}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold border-2 transition-all
                  ${
                    language === "en"
                      ? "border-blue-600 bg-blue-600 text-white"
                      : isDark
                        ? "border-gray-600 text-gray-300 hover:border-blue-600"
                        : "border-gray-200 text-gray-600 hover:border-blue-600"
                  }`}
              >
                English
              </button>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              {isDark ? (
                <MdOutlineDarkMode className="w-5 h-5 text-blue-600" />
              ) : (
                <MdOutlineLightMode className="w-5 h-5 text-blue-600" />
              )}
              <span className="text-sm font-semibold uppercase tracking-wider">
                {isBn ? "থিম" : "Theme"}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2">
                <MdOutlineLightMode className="w-4 h-4" />
                {isBn ? "লাইট" : "Light"}
              </span>

              <button
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none
                  ${isDark ? "bg-blue-600" : "bg-gray-300"}`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-300
                    ${isDark ? "translate-x-7" : "translate-x-0"}`}
                />
              </button>

              <span className="text-sm flex items-center gap-2">
                <MdOutlineDarkMode className="w-4 h-4" />
                {isBn ? "ডার্ক" : "Dark"}
              </span>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-5 w-5 rounded-full bg-blue-600" />
              <span className="text-sm font-semibold uppercase tracking-wider">
                {isBn ? "রং" : "Theme Color"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {THEME_COLOR_OPTIONS.map((option) => {
                const selected = themeColor === option.key;

                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => changeThemeColor(option.key)}
                    className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2 text-left text-sm font-semibold transition-all
                      ${
                        selected
                          ? "border-blue-600 text-blue-600"
                          : isDark
                            ? "border-gray-600 text-gray-300 hover:border-blue-600"
                            : "border-gray-200 text-gray-600 hover:border-blue-600"
                      }`}
                  >
                    <span
                      className="h-4 w-4 rounded-full ring-2 ring-white/80 shadow-sm"
                      style={{ backgroundColor: option.swatch }}
                    />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPanel;
